import { Link } from 'react-router';
import { PiArrowSquareOutFill, PiNotePencilFill } from 'react-icons/pi';

import { Checkbox } from '#/modules/commonComponents/checkbox';

import ColumnTipHeader from './ColumnTipHeader';
import TableRowActionsMenu from './TableRowActionsMenu';

function resolveRowCheckboxState(row) {
  if (row.subRows?.length > 0) {
    if (row.getIsAllSubRowsSelected()) {
      return true;
    }
    if (row.getIsSomeSelected() && !row.getIsAllSubRowsSelected()) {
      return 'indeterminate';
    }
    return false;
  }

  return row.getIsSelected();
}

function handleRowCheckboxChange(row, value) {
  if (row.subRows?.length > 0) {
    row.toggleSelected(value === true, { selectChildren: true });
    return;
  }

  row.toggleSelected(value === true);
}

/**
 * 表頭欄位名 + 問號 Tooltip（hover / focus）。
 * 用於 columnDef.header；問號 icon 不觸發排序（見 ColumnTipHeader stopPropagation）。
 *
 * @param {string} label
 * @param {string} tipText
 * @returns {() => JSX.Element}
 */
export function createTipHeader(label, tipText) {
  function TipHeader() {
    return <ColumnTipHeader label={label} tip={tipText} />;
  }

  return TipHeader;
}

/**
 * 列操作欄（v1 風格編輯連結）。點擊不觸發列選取。
 * settings 等僅編輯、無獨立 detail 列表操作時使用。
 *
 * @param {object} options
 * @param {(row: object) => string} options.getEditPath
 * @param {number} [options.size=80]
 * @param {number} [options.minSize=80]
 */
export function createEditLinkColumn({
  getEditPath,
  canShowEdit = () => true,
  size = 80,
  minSize = 80,
}) {
  return {
    id: 'actions',
    accessorKey: 'actions',
    header: '操作',
    size,
    minSize,
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: false,
    meta: {
      enableColumnOrder: false,
      headerClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => {
      if (!canShowEdit(row.original)) {
        return null;
      }

      return (
        <Link
          className="table_action_link"
          to={getEditPath(row.original)}
          onClick={(event) => event.stopPropagation()}
          aria-label="編輯"
        >
          <PiNotePencilFill size={24} aria-hidden />
        </Link>
      );
    },
  };
}

/**
 * 單據列表雙 icon：外連→detail（永遠）；鉛筆→edit（預設僅 DRAFT）。
 *
 * @param {object} options
 * @param {(row: object) => string} options.getDetailPath
 * @param {(row: object) => string} options.getEditPath
 * @param {(row: object) => boolean} [options.canShowEdit]
 * @param {number} [options.size=100]
 * @param {number} [options.minSize=100]
 */
export function createDocumentNavLinksColumn({
  getDetailPath,
  getEditPath,
  canShowEdit = (row) => row.status === 'DRAFT',
  size = 100,
  minSize = 100,
}) {
  return {
    id: 'actions',
    accessorKey: 'actions',
    header: '操作',
    size,
    minSize,
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: false,
    meta: {
      enableColumnOrder: false,
      headerClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    cell: ({ row }) => {
      const data = row.original;
      const showEdit = canShowEdit(data);

      return (
        <div className="flex justify-center gap-2.5">
          <Link
            className="table_action_link"
            to={getDetailPath(data)}
            onClick={(event) => event.stopPropagation()}
            aria-label="詳情"
          >
            <PiArrowSquareOutFill size={24} aria-hidden />
          </Link>
          {showEdit ? (
            <Link
              className="table_action_link"
              to={getEditPath(data)}
              onClick={(event) => event.stopPropagation()}
              aria-label="編輯"
            >
              <PiNotePencilFill size={24} aria-hidden />
            </Link>
          ) : null}
        </div>
      );
    },
  };
}

/**
 * 列操作欄（`...` + DropdownMenu）。點擊不觸發列選取。
 *
 * @param {object} options
 * @param {string} [options.id='actions']
 * @param {number} [options.size=56]
 * @param {number} [options.minSize=48]
 * @param {(row: object) => Array<{key?, label, onClick?, disabled?, destructive?}>} options.getActions
 */
export function createActionsColumn({ id = 'actions', size = 56, minSize = 48, getActions }) {
  return {
    id,
    accessorKey: id,
    header: '',
    size,
    minSize,
    enableSorting: false,
    enableResizing: false,
    meta: {
      enableColumnOrder: false,
    },
    cell: ({ row }) => (
      <TableRowActionsMenu actions={getActions ? getActions(row.original) : []} />
    ),
  };
}

/**
 * 選取欄（單選／多選；支援 expanding 父子 indeterminate）。
 *
 * @param {object} [options]
 * @param {string} [options.id='select']
 * @param {number} [options.size=48]
 * @param {boolean} [options.enableMultiSelection=false]
 * @param {boolean} [options.showHeaderCheckbox=true]
 */
export function createSelectColumn({
  id = 'select',
  size = 48,
  enableMultiSelection = false,
  showHeaderCheckbox = true,
} = {}) {
  return {
    id,
    accessorKey: id,
    header: showHeaderCheckbox && enableMultiSelection
      ? ({ table }) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(value) => table.toggleAllRowsSelected(value === true)}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            aria-label="全選"
          />
        </div>
      )
      : '',
    size,
    enableSorting: false,
    enableResizing: false,
    meta: {
      enableColumnOrder: false,
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Checkbox
          checked={resolveRowCheckboxState(row)}
          onCheckedChange={(value) => handleRowCheckboxChange(row, value)}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="選取列"
        />
      </div>
    ),
  };
}
